import React from 'react'
import type { AuditLogItem } from './AuditLogItem'
import { EventParentTypes } from '../../config'
import { Link } from 'react-router-dom'
import cx from 'classnames'
import { MaxWidthNameColumn } from '../../util/table'
import { NameText } from '../table/NameText'

interface TargetProps {
    auditLogItem: AuditLogItem
    screenshotMode: boolean
    maxWidth?: number
}

export const Target = ({ auditLogItem, screenshotMode, maxWidth }: TargetProps) => {
    switch (auditLogItem.target_type_id) {
        case EventParentTypes.CAMPAIGN:
            return (
                <CampaignText
                    maxWidth={maxWidth}
                    campaign={auditLogItem.target as Models.CampaignExtended}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.AD_GROUP:
            return (
                <AdGroupText
                    maxWidth={maxWidth}
                    adGroup={auditLogItem.target as Models.AdGroupExtended}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.KEYWORD:
            return (
                <KeywordText
                    maxWidth={maxWidth}
                    keyword={auditLogItem.target as Models.KeywordExtended}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.TARGETS:
            return (
                <TargetText
                    maxWidth={maxWidth}
                    target={auditLogItem.target as Models.TargetExtended}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.PRODUCT_AD:
            return (
                <ProductAdText
                    maxWidth={maxWidth}
                    productAd={auditLogItem.target as Models.ProductAdExtended}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.CampaignNegativeKeywords:
            return (
                <CampaignNegativeKeywordText
                    maxWidth={maxWidth}
                    campaignNegativeKeyword={
                        auditLogItem.target as Models.CampaignNegativeKeywordExtended
                    }
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.NegativeKeywords:
            return (
                <NegativeKeywordText
                    maxWidth={maxWidth}
                    negativeKeyword={auditLogItem.target as Models.NegativeKeywordExtended}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.NegativeTargets:
            return (
                <NegativeTargetText
                    maxWidth={maxWidth}
                    item={auditLogItem.target as Models.NegativeTargetExtended}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.Portfolios:
            return (
                <PortfolioText
                    maxWidth={maxWidth}
                    item={auditLogItem}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.Recipe:
            return (
                <RecipeText
                    maxWidth={maxWidth}
                    item={auditLogItem}
                    screenshotMode={screenshotMode}
                />
            )
        case EventParentTypes.Promotion:
            return (
                <PromotionText
                    maxWidth={maxWidth}
                    item={auditLogItem}
                    screenshotMode={screenshotMode}
                />
            )
        default:
            console.warn('Cannot resolve target for', auditLogItem)
            return null
    }
}

interface CampaignTextProps {
    campaign: Models.CampaignExtended
    screenshotMode: boolean
    maxWidth?: number
}

const CampaignText = ({ campaign, screenshotMode, maxWidth }: CampaignTextProps) => {
    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <TextLink
                to={`/campaigns/${campaign.campaign_id}`}
                nonLinkText={'Campaign'}
                linkText={campaign.name}
                screenshotMode={screenshotMode}
            />
        </div>
    )
}

interface AdGroupTextProps {
    adGroup: Models.AdGroupExtended
    screenshotMode: boolean
    maxWidth?: number
}

const AdGroupText = ({ adGroup, screenshotMode, maxWidth }: AdGroupTextProps) => {
    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <TextLink
                to={`/campaigns/${adGroup?.campaign_id}`}
                nonLinkText={'Campaign'}
                linkText={adGroup?.campaign_name}
                screenshotMode={screenshotMode}
            />
            <TextLink
                to={`/ad-groups/${adGroup?.ad_group_id}`}
                nonLinkText={'Ad Group'}
                linkText={adGroup?.name}
                screenshotMode={screenshotMode}
            />
        </div>
    )
}

interface KeywordTextProps {
    keyword: Models.KeywordExtended
    screenshotMode: boolean
    maxWidth?: number
}

const KeywordText = ({ keyword, screenshotMode, maxWidth }: KeywordTextProps) => {
    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <TextLink
                to={`/campaigns/${keyword.campaign_id}`}
                nonLinkText={'Campaign'}
                linkText={keyword.campaign_name}
                screenshotMode={screenshotMode}
            />
            <TextLink
                to={`/ad-groups/${keyword.ad_group_id}`}
                nonLinkText={'Ad Group'}
                linkText={keyword.ad_group_name}
                screenshotMode={screenshotMode}
            />
            <TextLink
                to={`/keywords/${keyword.keyword_id}`}
                nonLinkText={'Keyword'}
                linkText={keyword.keyword_text}
                screenshotMode={screenshotMode}
            />
        </div>
    )
}

interface TargetTextProps {
    target: Models.TargetExtended
    screenshotMode: boolean
    maxWidth?: number
}

const TargetText = ({ target, screenshotMode, maxWidth }: TargetTextProps) => {
    console.log('target is', target)
    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <TextLink
                to={`/campaigns/${target.campaign_id}`}
                nonLinkText={'Campaign'}
                linkText={target.campaign_name}
                screenshotMode={screenshotMode}
            />
            <TextLink
                to={`/ad-groups/${target.ad_group_id}`}
                nonLinkText={'Ad Group'}
                linkText={target.ad_group_name}
                screenshotMode={screenshotMode}
            />
            <TextLink
                to={`/targets/${target.target_id}`}
                nonLinkText={'Target'}
                linkText={target.targeting_expression}
                screenshotMode={screenshotMode}
            />
        </div>
    )
}

interface ProductAdTextProps {
    productAd: Models.ProductAdExtended
    screenshotMode: boolean
    maxWidth?: number
}

const ProductAdText = ({ maxWidth, productAd, screenshotMode }: ProductAdTextProps) => {
    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <TextLink
                to={`/campaigns/${productAd.campaign_id}`}
                nonLinkText={'Campaign'}
                linkText={productAd.campaign_name}
                screenshotMode={screenshotMode}
            />
            <TextLink
                to={`/ad-groups/${productAd.ad_group_id}`}
                nonLinkText={'Ad Group'}
                linkText={productAd.ad_group_name}
                screenshotMode={screenshotMode}
            />
            <div className={cx({ blur: screenshotMode })}>ASIN - {productAd.asin}</div>
        </div>
    )
}

interface CampaignNegativeKeywordTextProps {
    campaignNegativeKeyword: Models.CampaignNegativeKeywordExtended
    screenshotMode: boolean
    maxWidth?: number
}

const CampaignNegativeKeywordText = ({
    maxWidth,
    campaignNegativeKeyword,
    screenshotMode,
}: CampaignNegativeKeywordTextProps) => {
    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <TextLink
                to={`/campaigns/${campaignNegativeKeyword.campaign_id}`}
                nonLinkText={'Campaign'}
                linkText={campaignNegativeKeyword.campaign_name}
                screenshotMode={screenshotMode}
            />
            <div className={cx({ blur: screenshotMode })}>
                Keyword - {campaignNegativeKeyword.keyword_text}
            </div>
        </div>
    )
}

interface NegativeKeywordTextProps {
    negativeKeyword: Models.NegativeKeywordExtended
    screenshotMode: boolean
    maxWidth?: number
}

const NegativeKeywordText = ({
    negativeKeyword,
    screenshotMode,
    maxWidth,
}: NegativeKeywordTextProps) => {
    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <TextLink
                to={`/campaigns/${negativeKeyword.campaign_id}`}
                nonLinkText={'Campaign'}
                linkText={negativeKeyword.campaign_name}
                screenshotMode={screenshotMode}
            />
            <TextLink
                to={`/ad-groups/${negativeKeyword.ad_group_id}`}
                nonLinkText={'Ad Group'}
                linkText={negativeKeyword.ad_group_name}
                screenshotMode={screenshotMode}
            />
            <div className={cx({ blur: screenshotMode })}>
                Keyword - {negativeKeyword.keyword_text}
            </div>
        </div>
    )
}

interface NegativeTargetTextProps {
    item: Models.NegativeTargetExtended
    screenshotMode: boolean
    maxWidth?: number
}

const NegativeTargetText = ({ item, screenshotMode, maxWidth }: NegativeTargetTextProps) => {
    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <TextLink
                to={`/campaigns/${item.campaign_id}`}
                nonLinkText={'Campaign'}
                linkText={item.campaign_name}
                screenshotMode={screenshotMode}
            />
            <TextLink
                to={`/ad-groups/${item.ad_group_id}`}
                nonLinkText={'Ad Group'}
                linkText={item.ad_group_name}
                screenshotMode={screenshotMode}
            />
            <div className={cx({ blur: screenshotMode })}>Target - {item.targeting_expression}</div>
        </div>
    )
}

interface PortfolioTextProps {
    item: AuditLogItem
    screenshotMode: boolean
    maxWidth?: number
}

const PortfolioText = ({ item, screenshotMode, maxWidth }: PortfolioTextProps) => {
    if (!('previous_value_int' in item.meta && 'previous_value_string' in item.meta)) {
        console.warn('Cannot resolve PortfolioText for', item)
        return null
    }

    return (
        <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
            <div className={cx({ blur: screenshotMode })}>
                Portfolio - {item.meta.previous_value_string}
            </div>
        </div>
    )
}

interface RecipeTextProps {
    item: AuditLogItem
    screenshotMode: boolean
    maxWidth?: number
}

const RecipeText = ({ item, screenshotMode, maxWidth }: RecipeTextProps) => {
    const hasRecipe = 'target' in item

    if (hasRecipe) {
        const recipe = item.target as Models.Recipe
        return (
            <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
                <TextLink
                    to={`/recipes/${recipe.id}`}
                    nonLinkText={'Recipe'}
                    linkText={recipe.name}
                    screenshotMode={screenshotMode}
                />
            </div>
        )
    } else if ('previous_value_string' in item.meta) {
        return (
            <div style={{ maxWidth: MaxWidthNameColumn }}>
                <div className={cx({ blur: screenshotMode })}>
                    Recipe - {item.meta.previous_value_string}
                </div>
            </div>
        )
    } else {
        return null
    }
}

interface PromotionTextProps {
    item: AuditLogItem
    screenshotMode: boolean
    maxWidth?: number
}

const PromotionText = ({ item, screenshotMode, maxWidth }: PromotionTextProps) => {
    const hasPromotion = 'target' in item

    if (hasPromotion) {
        const promo = item.target as Models.Promotion
        return (
            <div style={{ maxWidth: maxWidth || MaxWidthNameColumn }}>
                <TextLink
                    to={`/smart-campaigns/${promo.id}`}
                    nonLinkText={'Smart Campaign'}
                    linkText={promo.name}
                    screenshotMode={screenshotMode}
                />
            </div>
        )
    } else if ('previous_value_string' in item.meta) {
        return (
            <div style={{ maxWidth: MaxWidthNameColumn }}>
                <div className={cx({ blur: screenshotMode })}>
                    Smart Campaign - {item.meta.previous_value_string}
                </div>
            </div>
        )
    } else {
        // no promotion
        return (
            <div style={{ maxWidth: MaxWidthNameColumn }}>
                <div className={cx({ blur: screenshotMode })}>Smart Campaign - deleted</div>
            </div>
        )
    }
}

interface TextLinkProps {
    to: string
    nonLinkText: string
    linkText: string
    screenshotMode: boolean
}

const TextLink = ({ to, nonLinkText, linkText, screenshotMode }: TextLinkProps) => {
    return (
        <NameText screenshotMode={screenshotMode} className={cx({ blur: screenshotMode })}>
            {nonLinkText} -&nbsp;
            <Link to={to}>{linkText}</Link>
        </NameText>
    )
}
